<template>
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
    <!-- Main Content -->
    <div class="lg:col-span-2 space-y-6">
      <!-- Order Overview -->
      <div class="card p-6">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-lg font-semibold text-gray-900">Order Overview</h2>
          <span 
            class="inline-flex px-3 py-1 text-sm font-medium rounded-full"
            :class="getStatusClass(order.status)"
          >
            {{ formatStatus(order.status) }}
          </span>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 class="text-sm font-medium text-gray-500 mb-2">Service Details</h3>
            <div class="space-y-2">
              <p class="text-sm"><strong>Service:</strong> {{ order.service_name || 'Custom Service' }}</p>
              <p class="text-sm"><strong>Package:</strong> {{ order.package_name || 'Custom Package' }}</p>
              <p class="text-sm"><strong>Order Date:</strong> {{ formatDate(order.created_at) }}</p>
              <p class="text-sm"><strong>Estimated Delivery:</strong> {{ getEstimatedDelivery() }}</p>
            </div>
          </div>
          
          <div>
            <h3 class="text-sm font-medium text-gray-500 mb-2">Client Information</h3>
            <div class="space-y-2">
              <p class="text-sm"><strong>Name:</strong> {{ order.client_name || 'Client Name' }}</p>
              <p class="text-sm"><strong>Email:</strong> {{ order.client_email || 'client@email.com' }}</p>
              <p class="text-sm"><strong>Company:</strong> {{ order.client_company || 'N/A' }}</p>
              <p class="text-sm"><strong>Project Manager:</strong> {{ order.requirements?.project_manager || 'N/A' }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Project Requirements -->
      <div class="card p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Project Requirements</h2>
        
        <div class="space-y-4">
          <div>
            <h3 class="text-sm font-medium text-gray-700 mb-2">Project Description</h3>
            <p class="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              {{ order.requirements?.project_description || 'No description provided.' }}
            </p>
          </div>
          
          <div v-if="order.requirements?.features_needed?.length">
            <h3 class="text-sm font-medium text-gray-700 mb-2">Required Features</h3>
            <div class="flex flex-wrap gap-2">
              <span 
                v-for="feature in order.requirements.features_needed" 
                :key="feature"
                class="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
              >
                {{ feature }}
              </span>
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 class="text-sm font-medium text-gray-700 mb-2">Budget Range</h3>
              <p class="text-sm text-gray-600">{{ order.requirements?.budget_range || 'Not specified' }}</p>
            </div>
            <div>
              <h3 class="text-sm font-medium text-gray-700 mb-2">Timeline Preference</h3>
              <p class="text-sm text-gray-600">{{ order.requirements?.timeline_preference || 'Not specified' }}</p>
            </div>
          </div>
          
          <div v-if="order.requirements?.reference_links">
            <h3 class="text-sm font-medium text-gray-700 mb-2">Reference Links</h3>
            <p class="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              {{ order.requirements.reference_links }}
            </p>
          </div>
          
          <div v-if="order.requirements?.special_requests">
            <h3 class="text-sm font-medium text-gray-700 mb-2">Special Requests</h3>
            <p class="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              {{ order.requirements.special_requests }}
            </p>
          </div>
          
          <div v-if="order.requirements?.additional_notes">
            <h3 class="text-sm font-medium text-gray-700 mb-2">Additional Notes</h3>
            <p class="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              {{ order.requirements.additional_notes }}
            </p>
          </div>
        </div>
      </div>

      <!-- Order Timeline -->
      <div class="card p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Order Timeline</h2>
        
        <div class="flow-root">
          <ul class="-mb-8">
            <li v-for="(event, index) in orderTimeline" :key="index">
              <div class="relative pb-8" :class="{ 'pb-0': index === orderTimeline.length - 1 }">
                <span 
                  v-if="index !== orderTimeline.length - 1"
                  class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                ></span>
                <div class="relative flex space-x-3">
                  <div>
                    <span 
                      class="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
                      :class="event.completed ? 'bg-green-500' : event.current ? 'bg-blue-500' : 'bg-gray-300'"
                    >
                      <svg v-if="event.completed" class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                      </svg>
                      <span v-else class="h-2.5 w-2.5 bg-white rounded-full"></span>
                    </span>
                  </div>
                  <div class="min-w-0 flex-1 pt-1.5">
                    <div>
                      <p class="text-sm font-medium text-gray-900">{{ event.title }}</p>
                      <p class="text-sm text-gray-500">{{ event.description }}</p>
                    </div>
                    <div class="mt-2 text-sm text-gray-500">
                      <time>{{ event.date ? formatDate(event.date) : 'Pending' }}</time>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Messages -->
      <div class="card p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Messages</h2>
        
        <div class="space-y-4">
          <!-- Message Input -->
          <div>
            <label for="message" class="sr-only">Message</label>
            <textarea
              id="message"
              rows="3"
              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Type your message here..."
              :value="newMessage"
              @input="$emit('update:newMessage', $event.target.value)"
            ></textarea>
          </div>
          <div class="flex justify-end">
            <button
              @click="$emit('sendMessage')"
              :disabled="!newMessage?.trim()"
              class="btn btn-primary btn-sm"
            >
              Send Message
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Sidebar -->
    <div class="space-y-6">
      <!-- Order Summary -->
      <div class="card p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
        
        <div class="space-y-3">
          <div class="flex justify-between text-sm">
            <span class="text-gray-600">Subtotal:</span>
            <span class="text-gray-900">${{ order.subtotal?.toLocaleString() || order.total_amount?.toLocaleString() }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-600">Tax:</span>
            <span class="text-gray-900">${{ calculateTax().toLocaleString() }}</span>
          </div>
          <div class="flex justify-between text-lg font-semibold pt-3 border-t border-gray-200">
            <span>Total:</span>
            <span>${{ calculateTotal().toLocaleString() }}</span>
          </div>
        </div>
        
        <!-- Payment Information -->
        <div v-if="order.payment_info" class="mt-6 pt-6 border-t border-gray-200">
          <h3 class="text-sm font-medium text-gray-700 mb-3">Payment Information</h3>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Status:</span>
              <span class="font-medium" :class="order.payment_info.status === 'paid' ? 'text-green-600' : 'text-yellow-600'">
                {{ formatStatus(order.payment_info.status) }}
              </span>
            </div>
            <div v-if="order.payment_info.method" class="flex justify-between">
              <span class="text-gray-600">Method:</span>
              <span class="text-gray-900">{{ order.payment_info.method }}</span>
            </div>
            <div v-if="order.payment_info.date" class="flex justify-between">
              <span class="text-gray-600">Date:</span>
              <span class="text-gray-900">{{ formatDate(order.payment_info.date) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="card p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        
        <div class="space-y-3">
          <button 
            v-if="isAdminView"
            @click="$emit('updateStatus')"
            class="w-full btn btn-primary flex items-center justify-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
            Update Status
          </button>
          
          <button 
            v-if="order && ['completed', 'processing'].includes(order.status)"
            @click="$emit('downloadInvoice')"
            class="w-full btn btn-outline flex items-center justify-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            Download Invoice
          </button>
          
          <button 
            v-if="order && ['approved', 'quote_sent'].includes(order.status) && !isAdminView"
            @click="$emit('proceedToPayment')"
            class="w-full btn btn-primary flex items-center justify-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
            </svg>
            Pay Now
          </button>
          
          <button 
            v-if="!isAdminView"
            @click="$emit('contactSupport')"
            class="w-full btn bg-green-500 hover:bg-green-600 text-white flex items-center justify-center gap-2"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            Contact Support
          </button>
        </div>
      </div>

      <!-- Developer Assignments (Admin Only) -->
      <div v-if="isAdminView && canManageTeam" class="card p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-gray-900">Developer Team</h2>
        </div>
        
        <!-- Assignment Form -->
        <div class="space-y-3 mb-6">
          <select 
            :value="selectedDeveloperId" 
            @input="$emit('update:selectedDeveloperId', $event.target.value)"
            class="form-input text-sm"
          >
            <option value="">Select Developer...</option>
            <option v-for="dev in userStore.developers" :key="dev.id" :value="dev.id">
              {{ dev.name }}
            </option>
          </select>
          <button 
            @click="$emit('assignDeveloper')" 
            :disabled="!selectedDeveloperId" 
            class="w-full btn btn-primary btn-sm py-2"
          >
            Assign to Project
          </button>
        </div>

        <!-- Assigned Developers List -->
        <div class="space-y-3">
          <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider">Assigned Members</h3>
          <div v-if="order.developers?.length" class="space-y-2">
            <div v-for="dev in order.developers" :key="dev.id" 
              class="flex items-center justify-between p-3 bg-gray-50 rounded-xl group hover:bg-white hover:shadow-sm transition-all duration-200 border border-transparent hover:border-blue-100">
              <div class="flex items-center">
                <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 border-2 border-white shadow-sm">
                  <span class="text-blue-600 text-xs font-bold">{{ dev.name?.charAt(0) }}</span>
                </div>
                <span class="text-sm font-semibold text-gray-700">{{ dev.name }}</span>
              </div>
              <button v-if="canManageTeam" @click="$emit('removeDeveloper', dev.id)" class="p-1.5 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
          <div v-else class="text-center py-4 px-2 border-2 border-dashed border-gray-100 rounded-xl">
            <p class="text-xs text-gray-400 italic">No team members assigned yet</p>
          </div>
        </div>
      </div>

      <!-- Order Progress -->
      <div class="card p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Progress</h2>
        
        <div class="mb-2 flex justify-between text-sm">
          <span class="text-gray-600">Completion</span>
          <span class="text-gray-900">{{ getProgressPercentage() }}%</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div 
            class="bg-primary-600 h-2 rounded-full transition-all duration-300"
            :style="{ width: getProgressPercentage() + '%' }"
          ></div>
        </div>
        
        <p class="text-xs text-gray-500 mt-2">
          {{ getProgressDescription() }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  order: Object,
  orderTimeline: Array,
  isAdminView: Boolean,
  userStore: Object,
  selectedDeveloperId: String,
  formatDate: Function,
  getEstimatedDelivery: Function,
  getStatusClass: Function,
  formatStatus: Function,
  getProgressPercentage: Function,
  getProgressDescription: Function,
  calculateTotal: Function,
  calculateTax: Function,
  newMessage: String,
  canManageTeam: Boolean
})

defineEmits([
  'viewInvoice',
  'downloadInvoice',
  'proceedToPayment',
  'downloadOrderSummary',
  'contactSupport',
  'updateStatus',
  'assignDeveloper',
  'removeDeveloper',
  'update:selectedDeveloperId',
  'sendMessage',
  'update:newMessage'
])
</script>
